import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../utils/api";
import { API_ENDPOINTS } from "../config/constants";
import { logout } from "./authSlice";

const initialState = {
    data: null,
    status: "idle",
    error: null,
    hasContext: false,
};

const getReadableError = (error, fallbackMessage) => {
    const detail = error.response?.data?.detail;

    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) => item?.msg || item?.type)
            .filter(Boolean);

        if (messages.length > 0) {
            return messages.join(" ");
        }
    }

    if (typeof detail === "string") {
        return detail;
    }

    return error.response?.data?.message || error.message || fallbackMessage;
};

export const fetchOrganizationContext = createAsyncThunk(
    "organizationContext/fetchOrganizationContext",
    async (_, thunkAPI) => {
        try {
            const response = await api.get(
                API_ENDPOINTS.GET_ORGANIZATION_BRAND_CONTEXT,
            );
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                return null;
            }

            return thunkAPI.rejectWithValue(
                getReadableError(
                    error,
                    "Failed to fetch organization context.",
                ),
            );
        }
    },
);

export const saveOrganizationContext = createAsyncThunk(
    "organizationContext/saveOrganizationContext",
    async (formData, thunkAPI) => {
        try {
            const response = await api.post(
                API_ENDPOINTS.CREATE_UPDATE_ORGANIZATION_BRAND_CONTEXT,
                formData,
            );

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableError(
                    error,
                    "Failed to save organization context.",
                ),
            );
        }
    },
);

const organizationContextSlice = createSlice({
    name: "organizationContext",
    initialState,
    reducers: {
        clearOrganizationContextError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrganizationContext.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchOrganizationContext.fulfilled, (state, action) => {
                state.data = action.payload;
                state.hasContext = Boolean(action.payload);
                state.status = "succeeded";
                state.error = null;
            })
            .addCase(fetchOrganizationContext.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
            })
            .addCase(saveOrganizationContext.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(saveOrganizationContext.fulfilled, (state, action) => {
                state.data = action.payload;
                state.hasContext = true;
                state.status = "succeeded";
                state.error = null;
            })
            .addCase(saveOrganizationContext.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
            })
            .addCase(logout, () => initialState);
    },
});

export const { clearOrganizationContextError } =
    organizationContextSlice.actions;

export default organizationContextSlice.reducer;
